/**
 * BillingPricing — Admin Pricing Components Tree View with version mutations
 *
 * Originally read-only (US5, AC-20 in 2026-03-08-billing-admin-ui).
 * Extended 2026-05-05 with admin write actions per AC-9..AC-12 of
 * 2026-05-05-billing-pricing-admin-ui spec:
 *   - „Dodaj wersję" — AddVersionModal
 *   - „Skróć ważność" — CloseVersionDialog (PATCH validTo)
 *   - „Usuń" — DeleteVersionDialog (DELETE with 5-guard backend)
 *   - „Wymień wersję" — AddVersionModal in atomic-replacement mode (CB-4)
 */
import React, { useState, useEffect, useRef } from "react";
import Layout from "../../../components/Layout";
import { EmptyState } from "../../../components/common/EmptyState";
import { useBillingAdminBrand } from "../../../hooks/useBillingAdminBrand";
import {
  usePricingComponentsQuery,
  usePricingComponentQuery,
} from "../../../hooks/useAdminProductCatalog";
import BillingBrandGuard from "./BillingBrandGuard";
import BillingBrandSelector from "./BillingBrandSelector";
import type {
  PricingComponent,
  PricingComponentVersion,
} from "../../../types/adminBilling";
import { formatCalculatorConfig } from "./components/formatCalculatorConfig";
import { AddVersionModal } from "./components/AddVersionModal";
import { CloseVersionDialog } from "./components/CloseVersionDialog";
import { DeleteVersionDialog } from "./components/DeleteVersionDialog";
import { VersionListItem } from "./components/VersionListItem";
import { CompositeVersionItem } from "./components/CompositeVersionItem";
import { useQueryClient } from "@tanstack/react-query";

const PAGE_LIMIT = 20;

/**
 * Returns true when the given validity window contains the current instant.
 * validFrom=null is treated as 0 (epoch); validTo=null means open-ended.
 */
function isVersionActiveNow(
  validFrom: string | null | undefined,
  validTo: string | null | undefined,
): boolean {
  const now = Date.now();
  const fromMs = validFrom ? new Date(validFrom).getTime() : 0;
  const toMs = validTo ? new Date(validTo).getTime() : Number.POSITIVE_INFINITY;
  return fromMs <= now && now < toMs;
}

// =============================================================================
// PRICING COMPONENT DETAIL MODAL
// =============================================================================

interface PricingDetailModalProps {
  componentId: string;
  brandId: string;
  onClose: () => void;
}

export const PricingDetailModal: React.FC<PricingDetailModalProps> = ({
  componentId,
  brandId,
  onClose,
}) => {
  const {
    data: component,
    isLoading,
    refetch,
  } = usePricingComponentQuery(componentId, brandId);
  const queryClient = useQueryClient();

  const [addingVersion, setAddingVersion] = useState(false);
  const [replacingVersion, setReplacingVersion] =
    useState<PricingComponentVersion | null>(null);
  const [closingVersion, setClosingVersion] =
    useState<PricingComponentVersion | null>(null);
  const [deletingVersion, setDeletingVersion] =
    useState<PricingComponentVersion | null>(null);

  const refreshAfterMutation = () => {
    void refetch();
    void queryClient.invalidateQueries({ queryKey: ["pricingComponents"] });
  };

  // UX MEDIUM batch E task 4: prevent race-clicks while a modal is open or
  // detail data is being fetched. All per-version action buttons are disabled
  // until both conditions are clear.
  const isAnyModalOpen =
    addingVersion ||
    replacingVersion !== null ||
    closingVersion !== null ||
    deletingVersion !== null;
  const actionsDisabled = isLoading || isAnyModalOpen;

  // Escape-to-close
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  // Auto-focus first interactive element on open (a11y)
  const firstFocusRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    firstFocusRef.current?.focus();
  }, []);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    );
  }

  if (!component) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-sm w-full mx-4 text-center">
          <p className="text-red-600 text-sm">
            Nie udało się załadować szczegółów komponentu.
          </p>
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-neutral-100 text-neutral-700 rounded hover:bg-neutral-200 text-sm"
          >
            Zamknij
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pricing-detail-modal-title"
        className="bg-white rounded-lg max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-4 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h3
              id="pricing-detail-modal-title"
              className="text-lg font-semibold text-neutral-900"
            >
              {component.name}
            </h3>
            <div className="flex gap-2 mt-1">
              <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">
                {component.type}
              </span>
              <span
                className={`text-xs px-2 py-0.5 rounded ${
                  component.isActive
                    ? "bg-green-100 text-green-700"
                    : "bg-neutral-100 text-neutral-500"
                }`}
              >
                {component.isActive ? "Aktywny" : "Nieaktywny"}
              </span>
            </div>
          </div>
          <button
            ref={firstFocusRef}
            onClick={onClose}
            aria-label="Zamknij"
            className="text-neutral-400 hover:text-neutral-600"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Info */}
          <div className="grid grid-cols-2 gap-4 bg-neutral-50 p-4 rounded-md">
            <div>
              <span className="text-xs font-medium text-neutral-500 uppercase">
                Kolejność
              </span>
              <p className="text-sm text-neutral-800 mt-0.5">
                {component.sortOrder}
              </p>
            </div>
            {component.parent && (
              <div>
                <span className="text-xs font-medium text-neutral-500 uppercase">
                  Rodzic
                </span>
                <p className="text-sm text-neutral-800 mt-0.5">
                  {component.parent.name}
                </p>
              </div>
            )}
          </div>

          {/* Versions section — discriminated union: COMPOSITE vs SIMPLE (AC-4, AC-8) */}
          {(() => {
            const isComposite = component.kind === "composite";

            if (isComposite) {
              // ----------------------------------------------------------------
              // COMPOSITE — read-only, no mutation buttons (AC-8)
              // ----------------------------------------------------------------
              const compositeVersions = component.compositeVersions ?? [];
              return (
                <>
                  {/* Header: count only, no "Dodaj wersję" (AC-8) */}
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-neutral-900">
                      Wersje ({compositeVersions.length})
                    </h4>
                  </div>

                  {compositeVersions.length === 0 ? (
                    <div className="text-center py-6 bg-neutral-50 rounded">
                      <p className="text-sm text-neutral-600">Brak wersji</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {/* Backend returns compositeVersions validFrom DESC — no client sort needed */}
                      {compositeVersions.map((version) => (
                        <CompositeVersionItem
                          key={version.id}
                          version={version}
                          isActive={isVersionActiveNow(
                            version.validFrom,
                            version.validTo,
                          )}
                        />
                      ))}
                    </div>
                  )}
                </>
              );
            }

            // ------------------------------------------------------------------
            // SIMPLE — existing version management with mutation buttons (AC-7)
            // ------------------------------------------------------------------
            return (
              <>
                {/* Versions header + Add CTA */}
                <div className="flex items-center justify-between">
                  <h4 className="text-sm font-semibold text-neutral-900">
                    Wersje ({component.versions?.length ?? 0})
                  </h4>
                  <button
                    type="button"
                    onClick={() => setAddingVersion(true)}
                    className="text-sm px-4 py-2 bg-primary-600 text-white rounded font-medium hover:bg-primary-700"
                  >
                    + Dodaj wersję
                  </button>
                </div>

                {(!component.versions || component.versions.length === 0) && (
                  <div className="text-center py-6 bg-neutral-50 rounded">
                    <p className="text-sm text-neutral-600">
                      Komponent nie ma żadnych wersji
                    </p>
                    <button
                      type="button"
                      onClick={() => setAddingVersion(true)}
                      className="mt-2 text-xs text-primary-600 hover:text-primary-800 underline"
                    >
                      Dodaj pierwszą wersję
                    </button>
                  </div>
                )}

                {component.versions && component.versions.length > 0 && (
                  <div className="space-y-3">
                    {[...component.versions]
                      .sort((a, b) => {
                        const av = a.validFrom
                          ? new Date(a.validFrom).getTime()
                          : 0;
                        const bv = b.validFrom
                          ? new Date(b.validFrom).getTime()
                          : 0;
                        return bv - av; // newest first
                      })
                      .map((version) => {
                        const isActive = isVersionActiveNow(
                          version.validFrom,
                          version.validTo,
                        );
                        const now = Date.now();
                        const fromMs = version.validFrom
                          ? new Date(version.validFrom).getTime()
                          : 0;
                        const seedManaged = version.isManagedBySeed === true;
                        // SSOT M-2 (ADR-068): trust backend capability flags. Fall
                        // back to legacy `!seedManaged && inFuture` only if the
                        // payload predates the wired backend (older deploys).
                        const inFuture = fromMs > now;
                        const canClose =
                          typeof version.canClose === "boolean"
                            ? version.canClose
                            : true;
                        const canDelete =
                          typeof version.canDelete === "boolean"
                            ? version.canDelete
                            : !seedManaged && inFuture;
                        // Replace = atomic close+insert; same gating as DELETE
                        // (target must be future and not seed-managed).
                        const canReplace = !seedManaged && inFuture;
                        const cannotDeleteReasons =
                          version.cannotDeleteReasons ?? [];
                        return (
                          <VersionListItem
                            key={version.id}
                            version={version}
                            isActive={isActive}
                            seedManaged={seedManaged}
                            canClose={canClose}
                            canDelete={canDelete}
                            canReplace={canReplace}
                            cannotDeleteReasons={cannotDeleteReasons}
                            disabled={actionsDisabled}
                            onClose={setClosingVersion}
                            onReplace={setReplacingVersion}
                            onDelete={setDeletingVersion}
                          />
                        );
                      })}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>

      {addingVersion && (
        <AddVersionModal
          componentId={componentId}
          brandId={brandId}
          onClose={() => setAddingVersion(false)}
          onSuccess={() => {
            setAddingVersion(false);
            refreshAfterMutation();
          }}
          onRefetch={refreshAfterMutation}
        />
      )}
      {replacingVersion && replacingVersion.version !== undefined && (
        <AddVersionModal
          componentId={componentId}
          brandId={brandId}
          replaceExistingVersion={{
            id: replacingVersion.id,
            version: replacingVersion.version,
            validFrom: replacingVersion.validFrom ?? undefined,
            validTo: replacingVersion.validTo ?? null,
          }}
          onClose={() => setReplacingVersion(null)}
          onSuccess={() => {
            setReplacingVersion(null);
            refreshAfterMutation();
          }}
          onRefetch={refreshAfterMutation}
        />
      )}
      {closingVersion && (
        <CloseVersionDialog
          version={closingVersion}
          brandId={brandId}
          onClose={() => setClosingVersion(null)}
          onSuccess={() => {
            setClosingVersion(null);
            refreshAfterMutation();
          }}
        />
      )}
      {deletingVersion && (
        <DeleteVersionDialog
          version={deletingVersion}
          brandId={brandId}
          onClose={() => setDeletingVersion(null)}
          onSuccess={() => {
            setDeletingVersion(null);
            refreshAfterMutation();
          }}
        />
      )}
    </div>
  );
};

// =============================================================================
// PRICING TREE ROW
// =============================================================================

interface PricingTreeRowProps {
  component: PricingComponent;
  depth?: number;
  onSelect: (id: string) => void;
}

const PricingTreeRow: React.FC<PricingTreeRowProps> = ({
  component,
  depth = 0,
  onSelect,
}) => {
  return (
    <tr
      className="hover:bg-neutral-50 cursor-pointer"
      onClick={() => onSelect(component.id)}
    >
      <td className="px-6 py-3">
        <div
          className="flex items-center"
          style={{ paddingLeft: `${depth * 24}px` }}
        >
          {depth > 0 && (
            <span className="text-neutral-300 mr-2 text-xs">└─</span>
          )}
          <span className="font-medium text-neutral-900 text-sm">
            {component.name}
          </span>
        </div>
      </td>
      <td className="px-6 py-3 whitespace-nowrap">
        <span className="text-xs bg-neutral-100 text-neutral-600 px-2 py-0.5 rounded">
          {component.type}
        </span>
      </td>
      <td className="px-6 py-3 whitespace-nowrap text-sm text-neutral-500">
        {component.sortOrder}
      </td>
      <td className="px-6 py-3 whitespace-nowrap">
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
            component.isActive
              ? "bg-green-100 text-green-800"
              : "bg-neutral-100 text-neutral-500"
          }`}
        >
          {component.isActive ? "Aktywny" : "Nieaktywny"}
        </span>
      </td>
      <td className="px-6 py-3 text-sm text-neutral-500">
        <div>
          {component.compositeVersionCount ?? component.versions?.length ?? 0}{" "}
          wersji
        </div>
        {component.versions &&
          component.versions.length > 0 &&
          component.versions[0].calculator && (
            <div className="text-xs text-neutral-600 mt-0.5 truncate max-w-xs">
              {
                formatCalculatorConfig(
                  component.versions[0].calculator.type,
                  component.versions[0].calculator.config,
                ).summary
              }
            </div>
          )}
      </td>
      <td
        className="px-6 py-3 whitespace-nowrap text-right text-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={() => onSelect(component.id)}
          className="text-primary-600 hover:text-primary-800 font-medium"
        >
          Szczegóły
        </button>
      </td>
    </tr>
  );
};

// =============================================================================
// MAIN PAGE
// =============================================================================

const BillingPricing: React.FC = () => {
  const { selectedBrand, brands, isLoading, selectBrand, isBrandRequired } =
    useBillingAdminBrand();

  const [page, setPage] = useState(0);
  const offset = page * PAGE_LIMIT;

  const {
    data,
    isLoading: isLoadingComponents,
    isError: isErrorComponents,
    refetch: refetchComponents,
  } = usePricingComponentsQuery(selectedBrand?.id ?? null, {
    limit: PAGE_LIMIT,
    offset,
  });

  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(
    null,
  );

  const components = data?.data ?? [];
  const total = data?.total ?? 0;
  const limit = data?.limit ?? PAGE_LIMIT;

  // Reset page when brand changes
  useEffect(() => {
    setPage(0);
  }, [selectedBrand?.id]);

  // Build tree structure (root = no parentId or parentId not in list)
  const rootComponents = components.filter(
    (c) => !c.parentId || !components.some((other) => other.id === c.parentId),
  );
  const childrenByParent: Record<string, PricingComponent[]> = {};
  components.forEach((c) => {
    if (c.parentId) {
      if (!childrenByParent[c.parentId]) {
        childrenByParent[c.parentId] = [];
      }
      childrenByParent[c.parentId].push(c);
    }
  });

  const renderRows = (
    comps: PricingComponent[],
    depth: number = 0,
  ): React.ReactNode[] => {
    const rows: React.ReactNode[] = [];
    comps.forEach((comp) => {
      rows.push(
        <PricingTreeRow
          key={comp.id}
          component={comp}
          depth={depth}
          onSelect={setSelectedComponentId}
        />,
      );
      const children = childrenByParent[comp.id];
      if (children && children.length > 0) {
        rows.push(...renderRows(children, depth + 1));
      }
    });
    return rows;
  };

  return (
    <Layout>
      <BillingBrandGuard
        isBrandRequired={isBrandRequired}
        brands={brands}
        isLoading={isLoading}
        onSelectBrand={selectBrand}
      >
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-2xl font-bold text-neutral-900">Cennik</h1>
              <p className="text-neutral-600 mt-1 text-sm">
                Komponenty cennikowe — zarządzanie wersjami
              </p>
            </div>
            <BillingBrandSelector
              brands={brands}
              selectedBrand={selectedBrand}
              onSelectBrand={selectBrand}
            />
          </div>

          {/* Table */}
          {isErrorComponents ? (
            <div className="p-6 text-center">
              <p className="text-red-600">
                Wystąpił błąd podczas ładowania danych.
              </p>
              <button
                onClick={() => refetchComponents()}
                className="mt-2 text-primary-700 underline"
              >
                Spróbuj ponownie
              </button>
            </div>
          ) : isLoadingComponents ? (
            <div className="flex items-center justify-center min-h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
            </div>
          ) : components.length === 0 ? (
            <div className="bg-white rounded-lg border border-neutral-200">
              <EmptyState
                title="Brak komponentów cennikowych"
                description="Nie znaleziono komponentów cennikowych dla wybranego brandu."
              />
            </div>
          ) : (
            <div className="bg-white shadow-sm rounded-lg border border-neutral-200">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-neutral-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Nazwa
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Typ
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Kolejność
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Wersje
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                        Akcje
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-neutral-200">
                    {renderRows(rootComponents)}
                  </tbody>
                </table>
              </div>
              {total > limit && (
                <div className="flex items-center justify-between px-6 py-4 border-t border-neutral-200 text-sm text-gray-600">
                  <span>
                    Wyświetlanie {offset + 1}-{Math.min(offset + limit, total)}{" "}
                    z {total} rekordów
                  </span>
                  <div className="flex gap-2">
                    <button
                      disabled={offset === 0}
                      onClick={() => setPage((p) => p - 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Poprzednia
                    </button>
                    <button
                      disabled={offset + limit >= total}
                      onClick={() => setPage((p) => p + 1)}
                      className="px-3 py-1 border rounded disabled:opacity-50"
                    >
                      Następna
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </BillingBrandGuard>

      {/* Detail Modal */}
      {selectedComponentId && selectedBrand && (
        <PricingDetailModal
          componentId={selectedComponentId}
          brandId={selectedBrand.id}
          onClose={() => setSelectedComponentId(null)}
        />
      )}
    </Layout>
  );
};

export default BillingPricing;
